import i18n, { currentLocale } from './i18n';
import { z } from 'zod';

/* ---------------------------------------------------------------------------
   לקוח HTTP.

   שתי החלטות:

   1. כל תשובה עוברת סכמת Zod. ה-API הוא גבול — טיפוס TypeScript לבדו
      הוא הבטחה בזמן קומפילציה על נתונים שמגיעים בזמן ריצה. שינוי בשרת
      שלא סונכרן ייתפס כאן, עם הודעה ברורה, ולא כ-`undefined is not an
      object` שלושה רכיבים למטה.

   2. הטוקן ב-`sessionStorage` ולא ב-`localStorage`. XSS מגיע לשניהם,
      אבל sessionStorage לא שורד סגירת טאב ולא משותף בין טאבים — מה
      שמצמצם את חלון הזמן. הפתרון הנכון הוא cookie מסוג HttpOnly;
      זה מסומן כשלב הבא ב-docs.
   --------------------------------------------------------------------------- */

const TOKEN_KEY = 'craftmind.token';

export const tokenStore = {
  get: (): string | null => {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string): void => {
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* מצב פרטי — נמשיך בלי התמדה */
    }
  },
  clear: (): void => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

/**
 * העסק של המשתמש המחובר (תת-הדומיין שלו), שנשמר אחרי כניסה.
 *
 * הממשק יושב על דומיין אחד לכל העסקים, ולכן הבקשה לא יכולה לדעת לאיזה עסק
 * היא שייכת מה-Host. זה לא סוד ולא הרשאה — רק "באיזה עסק לחפש"; השרת בודק
 * שהטוקן שייך לאותו עסק. ב-localStorage, כדי שכניסה הבאה תזכור אותו.
 */
const TENANT_KEY = 'craftmind.tenant';

export const tenantStore = {
  get: (): string | null => {
    try {
      return localStorage.getItem(TENANT_KEY);
    } catch {
      return null;
    }
  },
  set: (subdomain: string): void => {
    try {
      localStorage.setItem(TENANT_KEY, subdomain);
    } catch {
      /* מצב פרטי — נמשיך בלי התמדה */
    }
  },
  clear: (): void => {
    try {
      localStorage.removeItem(TENANT_KEY);
    } catch {
      /* ignore */
    }
  },
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly path?: string,
    /** קוד מכונה מהשרת (SEAT_LIMIT, BILLING_READ_ONLY, DOWNGRADE_BLOCKED, PAYMENT_FAILED...). */
    readonly code?: string,
    /** פרטים מובנים לצד הקוד (למשל מה לשנות לפני שנמוך). */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** האם כדאי להציע למשתמש לנסות שוב. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429 || this.status === 0;
  }
}

/**
 * התפקיד של הסשן הנוכחי, מחוץ ל-React — כדי ש-`request()` יוכל לבדוק אותו.
 * AuthProvider מעדכן אותו בכל שינוי סשן. בזיכרון בלבד: אחרי רענון הוא ריק
 * עד ש-/auth/me חוזר, ואז השרת (שאוכף בכל מקרה) הוא קו ההגנה.
 */
let currentRole: string | null = null;
export const sessionRoleStore = {
  get: (): string | null => currentRole,
  set: (role: string | null): void => {
    currentRole = role;
  },
};

/**
 * כתיבות שמותרות גם לצפייה בלבד: החשבון של המשתמש עצמו (סיסמה, 2FA, יציאה),
 * מדדי שימוש, ודפי הלקוח הציבוריים (שם הטוקן הוא ההרשאה, לא התפקיד), והשיחה עם העוזר.
 */
const VIEWER_WRITE_PATHS = [
  /^\/auth\//,
  /^\/public\//,
  /^\/analytics\/events$/,
  // העוזר: לדבר, לפתוח/למחוק שיחה ולבטל כרטיס — כן. לאשר או לערוך כרטיס — לא.
  /^\/assistant\/conversations(\/[^/]+(\/messages)?)?$/,
  /^\/assistant\/actions\/[^/]+\/cancel$/,
];

/** רשת ביטחון: צופה לא שולח כתיבה בכלל. השרת דוחה ממילא ב-403; כאן חוסכים את הסיבוב. */
function viewerBlocked(method: string, path: string): boolean {
  if (currentRole !== 'VIEWER' || method === 'GET' || method === 'HEAD') return false;
  const pathname = path.split('?')[0] ?? path;
  return !VIEWER_WRITE_PATHS.some((re) => re.test(pathname));
}

/** נורה כשהשרת מחזיר 401 — ה-router מאזין ומעביר להתחברות. */
export const UNAUTHORIZED_EVENT = 'craftmind:unauthorized';

/** נורה כשכתיבה נחסמה כי העסק במצב קריאה בלבד (402 BILLING_READ_ONLY) — AuthProvider מרענן את הסשן, והבאנר מופיע. */
export const BILLING_READ_ONLY_EVENT = 'craftmind:billing-read-only';

interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  schema?: z.ZodType<T, z.ZodTypeDef, unknown>;
  signal?: AbortSignal;
  /** מפתח אידמפוטנטיות לפעולות שמשנות מצב. */
  idempotencyKey?: string;
  /** מדיניות מטמון של הדפדפן. `no-cache` — לוודא מול השרת (למשל טקסטים שנערכו הרגע בקונסולה). */
  cache?: RequestCache;
  /** כותרות נוספות. משמש ל-X-Onboarding-Secret, שאין לו גוף לשבת בו. */
  headers?: Record<string, string>;
  /**
   * העסק שהבקשה מיועדת אליו. ברירת מחדל: העסק השמור (tenantStore).
   * `null` = בלי עסק בכלל — כך הכניסה נותנת לשרת למצוא את העסק לפי האימייל.
   */
  tenant?: string | null;
}

/**
 * בסיס ה-API.
 *
 * בפיתוח: נתיב יחסי, וה-proxy של Vite מעביר ל-localhost:3000. זה
 * גם שומר על ה-Host המקורי, שממנו השרת מזהה את הטננט.
 *
 * בפרודקשן: השרת אינו מתארח יחד עם ה-SPA (הוא מונוליט stateful עם
 * Postgres ו-Redis), ולכן צריך מקור מלא. VITE_API_URL מוגדר בזמן
 * הבנייה. אם הוא חסר — נופלים לנתיב יחסי, מה שעובד כשמגישים את
 * שניהם מאותו דומיין דרך Caddy.
 */
const BASE = `${(import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')}/v1`;

/** כתובת מלאה ל-API — לקישורים שהדפדפן פותח ישירות (PDF להורדה). */
export const apiUrl = (path: string) => `${BASE}${path}`;

/**
 * קובץ מאומת (PDF פנימי) — אי אפשר לפתוח קישור רגיל, כי הדפדפן לא שולח
 * את ה-Bearer. מביאים blob ופותחים אותו בלשונית חדשה.
 */
export async function openAuthedFile(path: string): Promise<void> {
  const headers: Record<string, string> = {};
  const token = tokenStore.get();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const tenant = tenantStore.get() ?? import.meta.env.VITE_TENANT;
  if (tenant) headers['X-Tenant'] = tenant;
  const res = await fetch(`${BASE}${path}`, { headers });
  if (!res.ok) throw new ApiError(res.status, i18n.t('common:errors.status', { status: res.status }), path);
  const url = URL.createObjectURL(await res.blob());
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function request<T = unknown>(path: string, opts: RequestOptions<T> = {}): Promise<T> {
  const { method = 'GET', body, schema, signal, idempotencyKey, headers: extra, cache } = opts;

  if (viewerBlocked(method, path)) throw new ApiError(403, i18n.t('common:errors.viewOnly'), path);

  // השפה: השרת מחזיר בה הודעות שגיאה וטקסטים (קטלוג תהליכים וכו').
  const headers: Record<string, string> = { Accept: 'application/json', 'Accept-Language': currentLocale() };

  // זיהוי הטננט.
  //
  // כשהממשק והשרת על אותו דומיין, ה-Host נושא את תת-הדומיין של
  // הטננט והשרת פותר אותו לבד. כשהם על דומיינים שונים — SPA ב-Vercel
  // מול API במקום אחר — ה-Host כבר לא נושא אותו, ולכן מצהירים במפורש.
  //
  // זו הצהרה בלבד, לא הרשאה: השרת עדיין דורש סיסמה תקפה של אותו
  // טננט בהתחברות, ו-JwtAuthGuard דוחה טוקן שה-tenantId שלו אינו תואם.
  // VITE_TENANT — רק לפיתוח ולבנייה ישנה; ב-selfhost הוא ריק והעסק נשמר בכניסה.
  const tenant = opts.tenant === undefined ? (tenantStore.get() ?? import.meta.env.VITE_TENANT) : opts.tenant;
  if (tenant) headers['X-Tenant'] = tenant;
  const token = tokenStore.get();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // FormData (העלאת קובץ): הדפדפן קובע את ה-boundary בעצמו.
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  if (extra) Object.assign(headers, extra);

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : multipart ? body : JSON.stringify(body),
      signal,
      cache,
      credentials: import.meta.env.VITE_API_URL ? 'include' : 'same-origin',
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, i18n.t('common:errors.network'), path);
  }

  if (res.status === 401) {
    tokenStore.clear();
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    throw new ApiError(401, i18n.t('common:errors.unauthorized'), path);
  }

  if (!res.ok) {
    let message = i18n.t('common:errors.status', { status: res.status });
    let code: string | undefined;
    let details: unknown;
    try {
      const payload: unknown = await res.json();
      if (typeof payload === 'object' && payload !== null) {
        if ('message' in payload && typeof payload.message === 'string') message = payload.message;
        if ('code' in payload && typeof payload.code === 'string') code = payload.code;
        if ('details' in payload) details = payload.details;
      }
    } catch {
      /* גוף שאינו JSON — נשארים עם הודעת ברירת המחדל */
    }
    if (res.status === 402 && code === 'BILLING_READ_ONLY') window.dispatchEvent(new CustomEvent(BILLING_READ_ONLY_EVENT));
    throw new ApiError(res.status, message, path, code, details);
  }

  if (res.status === 204) return undefined as T;

  const data: unknown = await res.json();

  if (!schema) return data as T;

  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    // חוזה שבור בין שרת ללקוח. רועש בכוונה — עדיף מ-undefined שמתפשט.
    console.error('API response failed validation', { path, issues: parsed.error.issues });
    throw new ApiError(500, i18n.t('common:errors.badResponse'), path);
  }
  return parsed.data;
}
